import type { ConfirmCategoryResult } from '@/types/category-budget';

/** A toast to raise, and how loudly. */
export interface RetroApplyFeedback {
  message: string;
  /** Partial or total failure is a warning, matching the batch paths' precedent. */
  severity: 'success' | 'warning';
}

/**
 * What a single confirm/categorize owes the user, or null when it owes nothing.
 *
 * The batch paths have always toasted their counts while the single path — the one that
 * actually applies retroactively — said nothing. That asymmetry only got worse with the
 * merchant anchor, whose retro-apply reaches every descriptor variant of the identity:
 * the count is the honest counterweight to the wider blast radius, so it is reported
 * where the write happened.
 *
 * Silent only when NOTHING was attempted. A rule that changed no past row is exactly what
 * the checkbox said it would do, and a toast for it would be noise on every ordinary
 * confirm — but rows that were attempted and FAILED are the opposite case: staying quiet
 * there reads as "nothing needed doing" when the truth is "nothing worked".
 */
export function retroApplyFeedback(
  result: ConfirmCategoryResult | null | undefined,
): RetroApplyFeedback | null {
  if (!result) return null;
  const { retroAppliedCount: applied, retroFailedCount: failed } = result;
  if (applied < 1 && failed < 1) return null;

  const head = result.ruleCreated ? 'Regra criada — ' : '';
  if (applied < 1) {
    return {
      message: `${head}${
        failed === 1
          ? 'não foi possível recategorizar o lançamento anterior'
          : `não foi possível recategorizar os ${failed} lançamentos anteriores`
      }.`,
      severity: 'warning',
    };
  }

  const appliedText =
    applied === 1
      ? '1 lançamento anterior recategorizado'
      : `${applied} lançamentos anteriores recategorizados`;
  // Failures are named rather than folded into the count: a Pluggy round-trip can drop
  // rows, and "N recategorizados" alone would quietly overstate what landed.
  const failedText =
    failed > 0 ? `, ${failed} ${failed === 1 ? 'falhou' : 'falharam'}` : '';
  return {
    message: `${head}${appliedText}${failedText}.`,
    severity: failed > 0 ? 'warning' : 'success',
  };
}
